import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api, PaginationResponse } from '@/api';
import { ProjectFilterSearch } from '@/features/projects/filters';
import { Project, AssignedProjectUser } from '@/features/projects/types';
import { Sortable } from '@/ui/types';

export const PROJECTS_QUERY_KEY = 'projects';

export const projectsListQuery = ({
  organizationId,
  searchText = '',
  pageSize = 20,
  showArchived,
  filter,
  orderBy,
  orderDirection,
}: {
  organizationId: string;
  searchText?: string;
  pageSize?: number;
  showArchived?: boolean;
  filter?: ProjectFilterSearch;
} & Sortable<'CreatedAt' | 'LatestActivity'>) =>
  infiniteQueryOptions({
    queryKey: [
      PROJECTS_QUERY_KEY,
      organizationId,
      'infinite',
      searchText,
      pageSize,
      showArchived,
      filter,
      orderBy,
      orderDirection,
    ],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<PaginationResponse<Project>>('v1/projects', {
          params: {
            organizationId,
            searchText,
            page: pageParam,
            pageSize,
            showSoftArchived: showArchived,
            type: filter?.type,
            mode: filter?.mode,
            country: filter?.country,
            role: filter?.role,
            evaluations: filter?.evaluations,
            orderBy,
            orderDirection,
          },
        })
      ).data,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < lastPage.pageCount ? allPages.length + 1 : undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: 'always',
  });

export const projectsLiteQuery = (organizationId: string) =>
  queryOptions({
    queryKey: [PROJECTS_QUERY_KEY, organizationId, 'lite'],
    queryFn: async () =>
      (
        await api.get<{ id: string; name: string; type: string | null }[]>('v1/projects/lite', {
          params: { organizationId },
        })
      ).data,
  });

export const projectDetailsQuery = (projectId: string) =>
  queryOptions({
    staleTime: 900 * 1000,
    queryKey: ['projectDetails', projectId],
    queryFn: async () => (await api.get<Project>(`v1/projects/${projectId}`)).data,
  });

export const projectAccessUsersQuery = (projectId: string, organizationId: string) =>
  queryOptions({
    queryKey: ['projectsAccessUsers', projectId, organizationId],
    queryFn: async () =>
      (
        await api.get<{ users: AssignedProjectUser[] }>(
          `v1/project-access/${organizationId}/projects/${projectId}/users`,
        )
      ).data,
  });

export const projectAccessOrgUsersQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['projectsAccessOrgUsers', organizationId],
    queryFn: async () =>
      (await api.get<{ users: AssignedProjectUser[] }>(`v1/project-access/${organizationId}/users`))
        .data,
  });
